// @ts-self-types="./engine.d.ts"
/* ================= Narrative Studio — shared local analysis engine =================
   Deterministic. No model calls. This is the same engine both platforms run:
   Foundry uses it to extract evidence objects from canon; the end-user app uses it
   to decompose a user's own sources into a Narrative Unit.
   In production the seven-pass prompt chain replaces the extraction step and this
   engine remains as the deterministic gate layer. Nothing here is invented: every
   finding carries the sentence it came from.
==================================================================================== */
const NS = (function () {
  var win = typeof window !== 'undefined' ? window : {};

  /* ---------- lexicons (the controlled vocabulary the engine may use) ---------- */
  var FRAMES = {
    Scarcity: ['not enough', 'shortage', 'lack of', 'insufficient', 'scarce', 'scarcity', 'vacanc', 'unfilled', 'deficit', 'short of', 'too few', 'demand outstrips'],
    Pipeline: ['pipeline', 'training place', 'intake', 'supply of', 'recruit', 'throughput', 'entrants', 'graduat'],
    Growth: ['growth', 'expand', 'scale up', 'increase capacity', 'boost', 'grow the'],
    Efficiency: ['efficien', 'productivity', 'streamlin', 'value for money', 'optimis', 'optimiz', 'waste'],
    Crisis: ['crisis', 'emergency', 'breaking point', 'collapse', 'unsustainable', 'critical'],
    Resilience: ['resilien', 'coping', 'cope', 'withstand', 'endure', 'adapt to pressure'],
    Vocation: ['vocation', 'calling', 'dedication', 'they care', 'goodwill', 'commitment of staff', 'passion'],
    Security: ['security', 'safety', 'protect', 'risk to', 'safeguard', 'secure'],
    Fairness: ['fair', 'equit', 'unequal', 'inequalit', 'justice', 'deserv'],
    Choice: ['choice', 'freedom to', 'options', 'empower', 'autonomy'],
    Innovation: ['innovat', 'transform', 'digital', 'new model', 'reform', 'modernis', 'moderniz'],
    Sustainability: ['sustainab', 'long term', 'long-term', 'future generation', 'environmental'],
    Trust: ['trust in', 'confidence in', 'credib', 'legitima', 'faith in', 'mistrust'],
    Burden: ['burden', 'pressure on', 'strain', 'overwhelm', 'workload', 'backlog']
  };
  var ARCHS = {
    Institutional: ['policy', 'regulation', 'department', 'ministry', 'framework', 'statutory', 'mandate', 'legislation', 'plan sets out'],
    Economic: ['cost', 'funding', 'budget', 'market', 'price', 'spend', 'investment', 'economic', 'billion', 'million', 'pay'],
    Governance: ['accountab', 'oversight', 'governance', 'authority', 'regulator', 'compliance', 'enforc'],
    Knowledge: ['evidence', 'data', 'research', 'training', 'skills', 'education', 'expertise', 'learning'],
    Infrastructure: ['infrastructure', 'system', 'capacity', 'network', 'facility', 'estate', 'platform'],
    Financial: ['finance', 'credit', 'loan', 'insurance', 'subsid', 'debt', 'revenue'],
    Identity: ['identity', 'belonging', 'community', 'culture', 'profession', 'who we are'],
    Narrative: ['story', 'narrative', 'framing', 'described as', 'perception', 'rhetoric', 'language of'],
    Legitimacy: ['legitima', 'consent', 'mandate to', 'credibility', 'recognition', 'voice of'],
    Temporal: ['by 20', 'over the next', 'long term', 'short term', 'timeline', 'horizon', 'decade'],
    Spatial: ['region', 'local', 'urban', 'rural', 'area', 'place', 'geograph', 'nationwide']
  };
  var CAUSAL = ['because', 'leads to', 'lead to', 'results in', 'result in', 'causes', 'cause', 'drives', 'driving', 'so that', 'therefore', 'as a result', 'which means', 'in turn', 'consequently', 'thereby', 'due to', 'owing to', 'knock-on', 'feeds', 'reinforc'];
  var FUTURE = ['will ', 'would ', 'could ', 'may ', 'might ', 'plan to', 'plans to', 'aims to', 'aim to', 'intend', 'propose', 'plan for', 'by 20', 'plan sets out', 'plan is to', 'should '];
  var TYPO = {
    emerging: ['pilot', 'emerging', 'new approach', 'beginning to', 'early', 'trial', 'test', 'experiment'],
    strengthening: ['expand', 'increase', 'scale', 'accelerat', 'further', 'more of', 'double', 'boost', 'ramp'],
    weakening: ['declin', 'reduce', 'cut', 'fall', 'shrink', 'wind down', 'less of'],
    blocked: ['cannot', 'can no longer', 'blocked', 'prevent', 'restrict', 'barrier', 'closed', 'ban', 'no longer possible', 'ruled out'],
    competing: ['alternative', 'instead of', 'rather than', 'versus', 'compet', 'either'],
    entrenched: ['continue', 'established', 'long-standing', 'longstanding', 'existing', 'remains the', 'as always', 'default'],
    transformative: ['transform', 'fundamental', 'wholesale', 'radical', 'rethink', 'reimagine'],
    regressive: ['revert', 'roll back', 'return to', 'undo', 'reverse']
  };
  var ORG_TAIL = ['department', 'ministry', 'council', 'agency', 'trust', 'trusts', 'union', 'association', 'college', 'bank', 'commission', 'authority', 'board', 'committee', 'institute', 'service', 'group', 'party', 'company', 'society', 'federation', 'office', 'centre', 'center', 'foundation', 'network', 'alliance'];
  var ROLES = ['minister', 'ministers', 'secretary', 'manager', 'managers', 'worker', 'workers', 'staff', 'resident', 'residents', 'citizen', 'citizens', 'student', 'students', 'patient', 'patients', 'teacher', 'teachers', 'nurse', 'nurses', 'doctor', 'doctors', 'farmer', 'farmers', 'employer', 'employers', 'employee', 'employees', 'landlord', 'landlords', 'tenant', 'tenants', 'parent', 'parents', 'user', 'users', 'customer', 'customers', 'community', 'communities', 'volunteer', 'volunteers', 'carer', 'carers', 'supplier', 'suppliers', 'regulator', 'regulators', 'investor', 'investors', 'researcher', 'researchers'];
  var TENSION = ['but ', 'however', 'although', 'though ', 'despite', 'while ', 'whereas', 'in contrast', 'yet ', 'nevertheless', 'on the other hand', 'in practice'];
  var STAM = { Phenomenal: ['observed', 'reported', 'figures show', 'rate', 'number of', 'percent', '%'], Concept: ['means', 'defined', 'concept', 'idea of', 'notion'], Relational: ['between', 'relationship', 'depends on', 'linked to', 'connected'], System: ['system', 'process', 'mechanism', 'loop', 'feedback', 'structure'], Narrative: ['story', 'narrative', 'framing', 'described'], Lens: ['perspective', 'view', 'seen as', 'lens', 'from the point'], Strategic: ['strategy', 'plan', 'intervention', 'priority', 'objective'], Evolution: ['over time', 'historically', 'trend', 'shift', 'transition', 'evolving'] };

  /* ---------- text utilities ---------- */
  function clean(t) { return String(t || '').replace(/\r/g, '').replace(/[ \t]+/g, ' ').replace(/\n{3,}/g, '\n\n').trim(); }
  function words(t) { var m = clean(t).match(/[A-Za-z0-9£$%'’-]+/g); return m ? m.length : 0; }
  function sentences(t) {
    return clean(t).split(/\n\n+/).reduce(function (acc, para) {
      var parts = para.replace(/([.!?])\s+(?=[A-Z£"“(])/g, '$1\u0001').split('\u0001');
      parts.forEach(function (s) { s = s.trim(); if (s.length > 25 && /[a-z]/.test(s)) acc.push(s); });
      return acc;
    }, []);
  }
  function hits(lc, terms) { var n = 0; terms.forEach(function (t) { var i = 0; while ((i = lc.indexOf(t, i)) !== -1) { n++; i += t.length; } }); return n; }
  function firstWith(sents, terms) {
    for (var i = 0; i < sents.length; i++) { var lc = sents[i].toLowerCase(); for (var j = 0; j < terms.length; j++) if (lc.indexOf(terms[j]) !== -1) return { s: sents[i], term: terms[j] }; }
    return null;
  }
  function trim(s, n) { s = s.replace(/\s+/g, ' ').trim(); return s.length > n ? s.slice(0, n - 1).replace(/[,;:\s]\S*$/, '') + '…' : s; }
  function titleCase(s) { return s.charAt(0).toUpperCase() + s.slice(1); }
  function ordinal(n, a, b) { return n >= a ? 'high' : (n >= b ? 'medium' : 'low'); }

  /* ---------- file parsing ---------- */
  function readFile(file) {
    var name = file.name || 'source', ext = (name.split('.').pop() || '').toLowerCase();
    if (['txt', 'md', 'markdown', 'csv', 'tsv', 'json', 'log', 'rtf'].indexOf(ext) !== -1) {
      return file.text().then(function (t) { return { name: name, text: ext === 'rtf' ? t.replace(/\\[a-z]+\d*ise?/g, ' ').replace(/[{}]/g, ' ') : t, kind: kindOf(name, ext) }; });
    }
    if (ext === 'html' || ext === 'htm') {
      return file.text().then(function (t) {
        var d = document.createElement('div');
        d.innerHTML = t.replace(/<script[\s\S]*?<\/script>/gi, '').replace(/<style[\s\S]*?<\/style>/gi, '');
        return { name: name, text: d.textContent || '', kind: 'web page' };
      });
    }
    if (ext === 'docx') {
      return file.arrayBuffer().then(function (buf) {
        if (!win.JSZip) throw new Error('docx support did not load');
        return win.JSZip.loadAsync(buf).then(function (zip) {
          var f = zip.file('word/document.xml'); if (!f) throw new Error('not a Word document');
          return f.async('string').then(function (xml) {
            var t = xml.replace(/<w:p[ >][\s\S]*?(?=<w:p[ >]|$)/g, function (m) { return m + '\n'; })
              .replace(/<\/w:p>/g, '\n').replace(/<[^>]+>/g, '').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, "'");
            return { name: name, text: t, kind: kindOf(name, ext) };
          });
        });
      });
    }
    if (ext === 'pdf') {
      return file.arrayBuffer().then(function (buf) {
        if (!win.pdfjsLib) throw new Error('PDF support did not load — paste the text instead');
        return win.pdfjsLib.getDocument({ data: buf }).promise.then(function (pdf) {
          var pages = [];
          function page(i) {
            if (i > pdf.numPages) return Promise.resolve();
            return pdf.getPage(i).then(function (p) { return p.getTextContent(); }).then(function (c) {
              pages.push(c.items.map(function (it) { return it.str; }).join(' ')); return page(i + 1);
            });
          }
          return page(1).then(function () { return { name: name, text: pages.join('\n\n'), kind: kindOf(name, ext) }; });
        });
      });
    }
    return Promise.reject(new Error(ext.toUpperCase() + ' is not readable in the browser — paste the text instead'));
  }
  function kindOf(name, ext) {
    var n = name.toLowerCase();
    if (/interview|transcript/.test(n)) return 'transcript';
    if (/debate|hansard|minutes/.test(n)) return 'debate';
    if (/note|memo|field/.test(n)) return 'field note';
    if (/plan|policy|strategy|bill|act|consultation|report/.test(n)) return 'policy text';
    if (ext === 'csv' || ext === 'tsv') return 'dataset';
    return 'document';
  }

  /* ---------- the analysis ---------- */
  function analyse(sources, opts) {
    opts = opts || {};
    var all = sources.map(function (s) { return clean(s.text); });
    var joined = all.join('\n\n'), lcAll = joined.toLowerCase();
    var perSentence = sources.map(function (s) { return sentences(s.text); });
    var sents = [], sentSrc = [];
    perSentence.forEach(function (arr, i) { arr.forEach(function (s) { sents.push(s); sentSrc.push(sources[i].name); }); });

    /* frames */
    var frames = [];
    Object.keys(FRAMES).forEach(function (name) {
      var terms = FRAMES[name], total = 0, inSources = 0;
      all.forEach(function (t) { var h = hits(t.toLowerCase(), terms); total += h; if (h) inSources++; });
      if (!total) return;
      var f = firstWith(sents, terms);
      frames.push({
        name: name, hits: total, sources: inSources,
        desc: describeFrame(name, f && f.term),
        confidence: inSources >= 2 && total >= 5 ? 'high' : (total >= 3 ? 'medium' : 'low'),
        inference: lcAll.indexOf(name.toLowerCase()) === -1,
        quote: f ? trim(f.s, 240) : '', src: f ? sentSrc[sents.indexOf(f.s)] || sources[0].name : sources[0].name
      });
    });
    frames.sort(function (a, b) { return b.hits - a.hits || b.sources - a.sources; });
    frames = frames.slice(0, 6);
    if (frames.length) frames[0].dominant = true;

    /* actors */
    var counts = {};
    sents.forEach(function (s) {
      var m = s.match(/\b([A-Z][a-z]{2,}(?:\s+(?:of|for|and|the)?\s*[A-Z][A-Za-z]{2,}){0,3})\b/g) || [];
      m.forEach(function (c) {
        c = c.trim(); if (c.length < 4) return;
        var lcw = c.toLowerCase().split(' ');
        var isOrg = lcw.some(function (w) { return ORG_TAIL.indexOf(w) !== -1; }) || c.split(' ').length > 1 || /^[A-Z]{2,}$/.test(c);
        if (!isOrg) return;
        if (/^(The|This|That|These|Those|It|We|They|But|And|However|In|On|At|For|From|If|When|While|There|Our|Their)\b/.test(c)) return;
        counts[c] = (counts[c] || 0) + 1;
      });
      var lcs = s.toLowerCase();
      ROLES.forEach(function (r) { if (lcs.indexOf(r) !== -1) { var k = titleCase(r); counts[k] = (counts[k] || 0) + 1; } });
    });
    var isRole = function (k) { return ROLES.indexOf(k.toLowerCase()) !== -1; };
    var merged = {}, surface = {};
    Object.keys(counts).forEach(function (k) {
      var key = isRole(k) ? k.toLowerCase().replace(/s$/, '') : k;
      merged[key] = (merged[key] || 0) + counts[k];
      if (!surface[key] || counts[k] > counts[surface[key]]) surface[key] = k;
    });
    var show = function (k) { return surface[k] || k; };
    var ranked = Object.keys(merged).map(function (k) { return { name: k, n: merged[k] }; })
      .filter(function (a) { return isRole(a.name) ? a.n >= 2 : a.n >= 1 && a.name.split(' ').length > 1; })
      .sort(function (a, b) { return b.n - a.n; });
    var actors = ranked.slice(0, 10).map(function (a) { return { name: show(a.name), mentions: a.n, position: positionOf(show(a.name), sents) }; });
    var inActor = function (n) { return actors.some(function (a) { return a.name.toLowerCase().indexOf(n.toLowerCase()) !== -1; }); };
    var absent = Object.keys(merged).map(function (k) { return { name: k, n: merged[k] }; })
      .filter(function (a) { return a.n === 1 && isRole(a.name) && !inActor(a.name); })
      .slice(0, 2).map(function (a) { return { name: show(a.name), why: 'Mentioned once and never given a position — named as a category, not as a voice with interests.' }; });

    /* architectures */
    var architectures = [];
    Object.keys(ARCHS).forEach(function (name) {
      var h = hits(lcAll, ARCHS[name]); if (h < 2) return;
      var f = firstWith(sents, ARCHS[name]);
      architectures.push({ name: name, hits: h, note: f ? trim(f.s, 150) : '', provisional: ['Legitimacy', 'Temporal', 'Spatial'].indexOf(name) !== -1 });
    });
    architectures.sort(function (a, b) { return b.hits - a.hits; });
    architectures = architectures.slice(0, 5);

    /* loops — causal sentences, then a real closure check */
    var loops = [];
    sents.forEach(function (s) {
      var lc = s.toLowerCase();
      var c = CAUSAL.filter(function (t) { return lc.indexOf(t) !== -1; });
      if (!c.length) return;
      var i = lc.indexOf(c[0]);
      var head = trim(s.slice(0, i).replace(/[,;:\s]+$/, ''), 60), tail = trim(s.slice(i + c[0].length).replace(/^[,;:\s]+/, '').replace(/[.,;:\s]+$/, ''), 60);
      if (head.length < 12 || tail.length < 12) return;
      loops.push({ head: head, tail: tail, connective: c[0], quote: trim(s, 240), src: sentSrc[sents.indexOf(s)] });
    });
    var edges = loops.slice(0, 8);
    loops = edges.map(function (l) {
      var tailKey = keyTerm(l.tail);
      var closes = tailKey ? edges.some(function (o) { return o !== l && o.head.toLowerCase().indexOf(tailKey) !== -1; }) : false;
      var reinforcing = /more|increase|further|again|greater|worse|harder|fewer|less |additional|higher|widen|deeper/.test(l.tail.toLowerCase());
      return { name: l.head + ' → ' + l.tail, type: reinforcing ? 'reinforcing' : 'balancing', closes: closes, quote: l.quote, src: l.src,
        score: (closes ? 2 : 0) + (reinforcing ? 2 : 0) + Math.min(2, Math.floor((l.head.length + l.tail.length) / 60)) };
    }).sort(function (a, b) { return b.score - a.score; }).slice(0, 4);
    if (loops.length) loops[0].dominant = true;

    /* pathways */
    var pathways = [];
    sents.forEach(function (s) {
      if (pathways.length >= 5) return;
      var lc = s.toLowerCase();
      var typ = null;
      ['blocked', 'emerging', 'competing', 'transformative', 'regressive', 'weakening', 'strengthening', 'entrenched'].forEach(function (k) {
        if (!typ && TYPO[k].some(function (t) { return lc.indexOf(t) !== -1; })) typ = k;
      });
      if (!typ) return;
      if (typ !== 'blocked' && !FUTURE.some(function (t) { return lc.indexOf(t) !== -1; })) return;
      var name = pathwayName(s);
      if (!name) return;
      var key = name.toLowerCase().split(' ').slice(0, 2).join(' ');
      if (pathways.some(function (p) { return p.name.toLowerCase().indexOf(key) === 0 || key.indexOf(p.name.toLowerCase().split(' ').slice(0, 2).join(' ')) === 0; })) return;
      var enabling = [], constraining = [];
      var be = lc.indexOf('because'); if (be !== -1) enabling.push(trim(s.slice(be + 8), 60));
      ['despite', 'but ', 'unless', 'constrained by', 'limited by', 'without'].forEach(function (t) {
        var i = lc.indexOf(t); if (i !== -1 && constraining.length < 2) constraining.push(trim(s.slice(i + t.length), 60));
      });
      pathways.push({
        name: name, typology: typ,
        maturity: typ === 'emerging' ? 'emerging' : (typ === 'blocked' || typ === 'competing' ? 'potential' : 'consolidated'),
        enabling: enabling, constraining: constraining,
        confidence: constraining.length ? 'medium' : 'low',
        quote: trim(s, 240), src: sentSrc[sents.indexOf(s)]
      });
    });

    /* emerging issues — tension between two frames in one sentence */
    var emerging = [];
    sents.forEach(function (s) {
      if (emerging.length >= 3) return;
      var lc = s.toLowerCase();
      if (!TENSION.some(function (t) { return lc.indexOf(t) !== -1; })) return;
      var f = frames.filter(function (fr) { return FRAMES[fr.name].some(function (t) { return lc.indexOf(t) !== -1; }); });
      if (f.length < 2) return;
      emerging.push({ name: f[0].name + ' held against ' + f[1].name, desc: 'Two frames appear in tension in a single passage — an experience/narrative inconsistency, the earliest transition signature the framework watches for.', confidence: 'low', quote: trim(s, 240), src: sentSrc[sents.indexOf(s)] });
    });

    /* opportunities — derived strictly from what was found */
    var opportunities = [];
    if (frames[0]) opportunities.push({ logic: 'Attenuate', name: 'Weaken the ' + frames[0].name.toLowerCase() + ' frame by making its counter-evidence countable', desc: 'The ' + frames[0].name + ' frame carries ' + frames[0].hits + ' of the ' + frames.reduce(function (a, f) { return a + f.hits; }, 0) + ' frame markers in this material. A frame that dominates the counting usually survives because what would contradict it is never measured in the same place.' });
    if (absent[0]) opportunities.push({ logic: 'Re-seed', name: 'Give ' + absent[0].name.toLowerCase() + ' a position in the landscape', desc: 'An actor named once and never positioned cannot hold a pathway. Seeding their account changes what the landscape layer is able to represent.' });
    else if (loops[0]) opportunities.push({ logic: 'Attenuate', name: 'Break the dominant loop at its weakest edge', desc: 'The loop "' + loops[0].name + '" ' + (loops[0].closes ? 'closes structurally' : 'does not close in this material') + ', which is where an intervention would have to land.' });

    /* dark spots — real absences */
    var dark = [];
    if (!absent.length && actors.length) dark.push('No actor appears only once — either the landscape is genuinely complete, or the sources share one author’s cast of characters.');
    if (absent.length) dark.push(absent.map(function (a) { return a.name; }).join(' and ') + ' appear in the material but hold no position — a structural silence.');
    if (!loops.filter(function (l) { return l.closes; }).length && loops.length) dark.push('No feedback loop closes in this material: every causal chain runs one way and stops.');
    if (!pathways.filter(function (p) { return p.constraining.length; }).length && pathways.length) dark.push('No pathway names a constraining force — the material describes what will happen, never what would stop it.');
    var missingArch = Object.keys(ARCHS).filter(function (k) { return !architectures.some(function (a) { return a.name === k; }); });
    if (missingArch.indexOf('Legitimacy') !== -1) dark.push('Legitimacy is never at stake in this material — authority is assumed rather than argued for.');
    if (missingArch.indexOf('Temporal') !== -1) dark.push('No time horizon is named: the material has no before and no after.');
    if (!dark.length) dark.push('No structural silence detected at this corpus size — a larger corpus usually produces one.');

    /* STAM classification of chunks, used by Foundry */
    var stam = {};
    Object.keys(STAM).forEach(function (k) { stam[k] = 0; });
    sents.forEach(function (s) {
      var lc = s.toLowerCase(), best = null, bn = 0;
      Object.keys(STAM).forEach(function (k) { var h = hits(lc, STAM[k]); if (h > bn) { bn = h; best = k; } });
      if (best) stam[best]++;
    });

    return {
      sources: sources.map(function (s) { return { name: s.name, kind: s.kind || 'document', words: words(s.text) }; }),
      stats: { words: words(joined), sentences: sents.length, sourceCount: sources.length },
      meaning: { confidence: frames.length ? frames[0].confidence : 'low', frames: frames },
      landscape: { confidence: actors.length >= 4 ? 'high' : 'medium', actors: actors, absent: absent },
      architecture: { confidence: architectures.length >= 3 ? 'medium' : 'low', architectures: architectures, loops: loops },
      emerging: { confidence: 'low', items: emerging },
      pathways: { confidence: pathways.length ? (pathways.filter(function (p) { return p.constraining.length; }).length ? 'medium' : 'low') : 'low', items: pathways },
      opportunities: { confidence: 'medium', items: opportunities },
      darkSpots: dark, stam: stam, corrections: []
    };
  }

  function describeFrame(name, term) {
    var d = {
      Scarcity: 'The situation is described as a shortage of a resource rather than as a system producing loss.',
      Pipeline: 'Supply is treated as a chain with an input and an output, and no leakage term.',
      Growth: 'More of the same is presented as the direction of travel.',
      Efficiency: 'The problem is posed as one of waste rather than of purpose.',
      Crisis: 'Urgency is used to foreclose deliberation about alternatives.',
      Resilience: 'Those inside the system are cast as responsible for absorbing its pressure.',
      Vocation: 'The work is framed as a calling, which makes its terms a moral rather than structural question.',
      Security: 'Risk and protection organise what counts as a reasonable response.',
      Fairness: 'Distribution is the axis on which claims are judged.',
      Choice: 'Agency is located in individual selection rather than in structure.',
      Innovation: 'Novelty stands in for direction; the new is treated as self-evidently better.',
      Sustainability: 'A long horizon is invoked to justify present constraint.',
      Trust: 'Legitimacy and confidence are treated as the binding problem.',
      Burden: 'Load and strain organise the account, locating the problem in volume.'
    }[name];
    return d + (term ? ' Marker: “' + term.trim() + '”.' : '');
  }
  function positionOf(name, sents) {
    var lc = name.toLowerCase();
    for (var i = 0; i < sents.length; i++) { var s = sents[i]; if (s.toLowerCase().indexOf(lc) !== -1) return trim(s, 120); }
    return '';
  }
  function keyTerm(s) {
    var m = clean(s).toLowerCase().match(/[a-z]{5,}/g);
    if (!m) return null;
    var stop = ['which', 'there', 'their', 'would', 'about', 'because', 'those', 'these', 'other', 'where', 'while', 'being', 'after', 'before', 'could', 'should'];
    for (var i = 0; i < m.length; i++) if (stop.indexOf(m[i]) === -1) return m[i];
    return null;
  }
  var NAME_STOP = ['the', 'a', 'an', 'of', 'to', 'in', 'and', 'for', 'by', 'with', 'more', 'its', 'their', 'this', 'that', 'be', 'been', 'is', 'are', 'was', 'were', 'internationally', 'substantively', 'annually', 'also', 'still', 'now', 'no', 'longer', 'not'];
  function tidyName(s) {
    var w = s.toLowerCase().replace(/[^a-z0-9\s-]/g, ' ').split(/\s+/).filter(Boolean);
    while (w.length && NAME_STOP.indexOf(w[w.length - 1]) !== -1) w.pop();
    while (w.length && NAME_STOP.indexOf(w[0]) !== -1) w.shift();
    w = w.slice(0, 5);
    while (w.length && NAME_STOP.indexOf(w[w.length - 1]) !== -1) w.pop();
    return w.length >= 2 ? titleCase(w.join(' ')) : null;
  }
  function pathwayName(s) {
    var c = clean(s);
    var n = c.match(/\b((?:[a-z-]{4,}\s+){1,3}(?:programme|program|scheme|reform|plan|model|approach|route|policy|initiative|pathway|strategy|agreement|agreements|recruitment|retention|places|settlement|working))\b/i);
    if (n) { var t = tidyName(n[1]); if (t) return t; }
    var m = c.match(/\b((?:expand|increase|reduce|introduce|develop|build|create|deliver|extend|establish|launch|pilot|scale|reform|replace|restrict|block|close|sponsor|recruit|train|invest|narrow)[a-z]*\s+(?:[a-z-]+\s+){0,4}[a-z-]{4,})/i);
    if (m) { var t2 = tidyName(m[1]); if (t2) return t2; }
    return null;
  }

  return { analyse: analyse, readFile: readFile, sentences: sentences, words: words, clean: clean, kindOf: kindOf, FRAMES: FRAMES, ARCHS: ARCHS, STAM: STAM, hits: hits, trim: trim };
})();

export default NS;
export const analyse = NS.analyse, readFile = NS.readFile, sentences = NS.sentences, words = NS.words, clean = NS.clean, kindOf = NS.kindOf, hits = NS.hits, trim = NS.trim, FRAMES = NS.FRAMES, ARCHS = NS.ARCHS, STAM = NS.STAM;
